import { atom } from "nanostores"
import { $cart } from "~/store/cart"
import { BaseCart } from "./base-cart"
// Reduces server calls if a product has a crazy number of images.
const MAX_IMAGES_COUNT = 6

// Extracts product handle from the product URL
const SHOPIFY_PRODUCT_URL_HANDLE_REGEX = /\/products\/(.[\w\d-+]+)/

const urlSanitizer = (url) => {
    if (typeof url === "string") {
        if (url.startsWith("//")) return `https:${url}`
        else if (url.toLocaleLowerCase().startsWith("http")) return url
        else console.error(`Not a valid URL: ${url}`)
    } else console.error(`Not a valid URL: ${url}`)
    return null
}

export class ShopifyCart extends BaseCart {
    constructor($cart, $products) {
        super()
        this.$products = $products
        this.$cart = $cart
        this.cartId = undefined
        this.currency = window.Shopify?.currency?.active
        this.eventManager = undefined
        this.baseElement = undefined
        this.videoId = undefined
        this.playlistId = undefined
    }

    get products() {
        return this.$products.get()
    }

    syncCartState() {
        return this.getCartState().then((cartState) => {
            this.syncCartProperties(cartState)
            this.$cart.set(cartState)
        })
    }

    getCartId() {
        return this.cartId
    }

    getCurrency() {
        return this.currency
    }

    setBaseInfo(data) {
        this.baseElement = data?.baseElement
        this.videoId = data?.videoId
        this.playlistId = data?.playlistId
        this.eventManager = data?.eventManager
    }

    async getCartState() {
        const shopifyCart = await fetch("/cart.js", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((resp) => resp.json())
            .catch(() => ({}))

        this.currency = shopifyCart.currency
        if (shopifyCart.items.length > 0) {
            this.cartId = shopifyCart.token
        }
        return (shopifyCart.items || []).map((item) => ({
            id: item.variant_id.toString(),
            productSku: item.id.toString(),
            name: item.title,
            image: item.image,
            price: item.discounted_price / 100,
            description: item.product_description,
            quantity: item.quantity,
            variantTitle: item.variant_title,
            properties: this.getCartProperties(item.variant_id.toString()),
        }))
    }

    async fetchProduct(productUrl, additionalData = {}) {
        const handle = SHOPIFY_PRODUCT_URL_HANDLE_REGEX.exec(productUrl)
        if (!handle || (typeof handle[1] !== "string" && handle[1].length > 0)) {
            console.error("The provided URL is invalid")
            return
        }
        const shopifyProduct = await fetch("/products/" + handle[1] + ".js", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((resp) => resp.json())
            .catch(() => {})
        if (!shopifyProduct) return null
        return {
            productSku: shopifyProduct.id.toString(),
            image: shopifyProduct.featured_image,
            name: shopifyProduct.title,
            description: shopifyProduct.description,
            isSynced: true,
            options: shopifyProduct.options || [],
            variants: shopifyProduct.variants.map((variation) => ({
                id: variation.id.toString(),
                name: variation.name,
                promotionPrice: variation.compare_at_price ? variation.price / 100 : null,
                price: variation.compare_at_price ? variation.compare_at_price / 100 : variation.price / 100,
                options: variation.options || [],
                images: [
                    // Adding the featured image of the chosen variation (if existed) at the beginning of the images array
                    ...(variation.featured_image ? [variation.featured_image.src] : []),
                    // Adding product imgaes
                    ...shopifyProduct.images
                        .slice(0, MAX_IMAGES_COUNT - 1)
                        .map((url) => urlSanitizer(url))
                        .filter((url) => typeof url === "string"),
                ],
            })),
            ...additionalData,
        }
    }

    async syncProducts(loraProducts) {
        const currentProducts = this.$products.get()
        const unsyncedProducts = loraProducts.filter(
            (product) =>
                !!product.productSku &&
                !!product.productLink &&
                !currentProducts.some((item) => item.productSku === product.productSku)
        )
        const newProducts = await Promise.all(
            unsyncedProducts.map((product) => this.fetchProduct(product.productLink, { cmsProductId: product.id }))
        ).then((res) => res.filter((newProduct) => !!newProduct))

        const totalProducts = [...currentProducts, ...newProducts]
        this.$products.set(totalProducts)

        // we only return products of the argument
        return loraProducts.map((product) => {
            const newProduct = totalProducts.find((item) => item.productSku === product.productSku)
            if (newProduct) return { ...product, ...newProduct, isUsingCartIntegrated: true }
            return product
        })
    }

    addToCart(itemId, quantity = 1, properties = {}) {
        return fetch("/cart/add.js", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                items: [
                    {
                        quantity,
                        id: parseInt(itemId),
                    },
                ],
            }),
        }).then(async (resp) => {
            if (!this.cartId) await this.syncCartState()
            this.persistCartProperties(itemId, properties)
            return resp.json()
        })
    }

    updateItemInCart({ id, quantity, properties }) {
        return fetch("/cart/change.js", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ id, quantity }),
        }).then((resp) => {
            if (quantity === 0) this.deleteCartProperties(id)
            else this.persistCartProperties(id, properties)
            return resp.json()
        })
    }

    goCheckout() {
        window.open(window.location.origin + "/cart", "_blank")
    }
}

const $products = atom([])

const shopifyCart = new ShopifyCart($cart, $products)

export default shopifyCart
